/**
 * UpsForm — Ficha de detalle de un SAI (UPS), torre o rack.
 *
 * Sigue el patrón de RouterForm / FirewallForm:
 *  - barra superior con Guardar / Descartar / Eliminar
 *  - smart buttons (formato, potencia, batería, ubicación, documentos…)
 *  - pestañas General / Potencia / Baterías / Gestión / Ubicación / Alimentación
 *  - documentación mediante DocSmartButton (element_type='ups')
 *  - panel de actividad del CI (ciType='ups')
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  BoltIcon,
  Battery50Icon,
  CpuChipIcon,
  MapPinIcon,
  ServerStackIcon,
  SparklesIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import LocationTreeSelect from './common/LocationTreeSelect';
import DeviceAccessPanel from './DeviceAccessPanel';
import PowerConnectionsPanel from './PowerConnectionsPanel';
import UpsOutletsPanel from './UpsOutletsPanel';
import FormCardsShell, { Sf2Card } from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import '../styles/DetailView.css';
import './ServerFormCards.css';
import DetailCrumb from './DetailCrumb';

// ── Constantes ────────────────────────────────────────────────────────────────

const FORM_FACTORS = [
  { value: 'rack',  label: 'Rack (ocupa U en un armario)' },
  { value: 'tower', label: 'Torre (equipo independiente)' },
];

const TECHNOLOGIES = [
  { value: 'offline',          label: 'Offline / Standby' },
  { value: 'line_interactive', label: 'Line-Interactive' },
  { value: 'online',           label: 'Online (doble conversión)' },
  { value: 'other',            label: 'Otra' },
];

const BATTERY_TYPES = [
  { value: 'vrla',    label: 'VRLA / plomo-ácido sellada' },
  { value: 'lithium', label: 'Litio (Li-ion / LFP)' },
  { value: 'nicd',    label: 'NiCd' },
  { value: 'other',   label: 'Otra' },
];

const PHASES = [
  { value: 'single', label: 'Monofásico' },
  { value: 'three',  label: 'Trifásico' },
];

const STATUS_COLORS = {
  active:      { bg: '#dcfce7', color: '#15803d', label: 'Activo' },
  standby:     { bg: '#e0f2fe', color: '#0369a1', label: 'Standby' },
  maintenance: { bg: '#fef9c3', color: '#854d0e', label: 'Mantenimiento' },
  decom:       { bg: '#fee2e2', color: '#991b1b', label: 'Decom' },
};

const EMPTY_FORM = {
  name: '',
  vendor: '',
  model: '',
  serial_number: '',
  asset_tag: '',
  form_factor: 'rack',
  technology: 'line_interactive',
  capacity_va: '',
  capacity_w: '',
  current_load_w: '',
  phases: 'single',
  input_voltage: '230',
  output_voltage: '230',
  outlets_count: '',
  outlet_types: '',
  has_bypass: false,
  battery_type: 'vrla',
  battery_quantity: '',
  battery_runtime_min: '',
  battery_install_date: '',
  battery_replace_date: '',
  network_card: false,
  management_ip: '',
  management_mac: '',
  firmware_version: '',
  purchase_date: '',
  warranty_end: '',
  supplier: '',
  status: 'active',
  notes: '',
  // Colocación física
  location_id: '',
  location_name: '',
  rack_id: '',
  rack_name: '',
  size_u: 1,
  position_u: '',
};

function num(value) {
  return value === '' || value === null || value === undefined ? null : Number(value);
}

function dateOrNull(value) {
  return value ? value : null;
}

// ── Componente ────────────────────────────────────────────────────────────────

export default function UpsForm({ ups, companyId, onClose, onSave, variant = 'classic', navItems = [], navIndex = -1, onNavigate }) {
  const isCards = variant === 'cards';
  const isEditing = Boolean(ups?.id);

  const [form, setForm] = useState(EMPTY_FORM);
  const [tab, setTab] = useState('general');
  const [accessCount, setAccessCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [error, setError] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  // Cambia para recargar el formulario desde el SAI guardado («Descartar»).
  const [reloadKey, setReloadKey] = useState(0);

  // Cambios sin guardar al pasar de ficha o cerrar.
  const navGuard = useNavGuard({
    dirty: isDirty,
    saving,
    entity: 'este SAI',
    actionLabel: 'pasas a otro SAI o cierras la ficha',
    save: () => handleSave(),
  });

  // Colocación
  const [locations, setLocations] = useState([]);
  const [racks, setRacks] = useState([]);
  const [rackFreeUnits, setRackFreeUnits] = useState(null);

  // Catálogo marca/modelo
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);

  // Alta de marca / modelo en el maestro de la empresa
  const [addingManufacturer, setAddingManufacturer] = useState(false);
  const [newManufacturer, setNewManufacturer] = useState('');
  const [addingModel, setAddingModel] = useState(false);
  const [newModel, setNewModel] = useState('');
  const [catalogError, setCatalogError] = useState(null);
  const [catalogSaving, setCatalogSaving] = useState(false);

  // ── Bootstrap ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (ups) {
      setForm({
        name:                   ups.name || '',
        vendor:                 ups.vendor || '',
        model:                  ups.model || '',
        serial_number:          ups.serial_number || '',
        asset_tag:              ups.asset_tag || '',
        form_factor:            ups.form_factor || 'rack',
        technology:             ups.technology || 'line_interactive',
        capacity_va:            ups.capacity_va ?? '',
        capacity_w:             ups.capacity_w ?? '',
        current_load_w:         ups.current_load_w ?? '',
        phases:                 ups.phases || 'single',
        input_voltage:          ups.input_voltage || '',
        output_voltage:         ups.output_voltage || '',
        outlets_count:          ups.outlets_count ?? '',
        outlet_types:           ups.outlet_types || '',
        has_bypass:             !!ups.has_bypass,
        battery_type:           ups.battery_type || '',
        battery_quantity:       ups.battery_quantity ?? '',
        battery_runtime_min:    ups.battery_runtime_min ?? '',
        battery_install_date:   ups.battery_install_date || '',
        battery_replace_date:   ups.battery_replace_date || '',
        network_card:           !!ups.network_card,
        management_ip:          ups.management_ip || '',
        management_mac:         ups.management_mac || '',
        firmware_version:       ups.firmware_version || '',
        purchase_date:          ups.purchase_date || '',
        warranty_end:           ups.warranty_end || '',
        supplier:               ups.supplier || '',
        status:                 ups.status || 'active',
        notes:                  ups.notes || '',
        location_id:            ups.location_id ?? '',
        location_name:          ups.location_name || '',
        rack_id:                ups.rack_id ?? '',
        rack_name:              ups.rack_name || '',
        size_u:                 ups.size_u ?? 1,
        position_u:             ups.position_u ?? '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setIsDirty(false);
  }, [ups, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Catálogos, ubicaciones y racks
  const loadAux = useCallback(async () => {
    if (!companyId) return;
    try {
      const [locRes, rackRes, mfrRes, modRes] = await Promise.all([
        api.getLocations('', companyId).catch(() => ({ data: [] })),
        api.getRacks(null, companyId).catch(() => ({ data: [] })),
        api.getManufacturers(companyId, { deviceType: 'network' }).catch(() => ({ data: [] })),
        api.getDeviceModels(companyId, { deviceType: 'network' }).catch(() => ({ data: [] })),
      ]);
      setLocations(locRes.data || []);
      setRacks(rackRes.data || []);
      setManufacturerOptions(Array.isArray(mfrRes?.data) ? mfrRes.data : []);
      setModelOptions(Array.isArray(modRes?.data) ? modRes.data : []);
    } catch (_) { /* sin catálogo: los campos siguen editables */ }
  }, [companyId]);

  useEffect(() => { loadAux(); }, [loadAux]);

  // Si ya tiene rack, cargar sus huecos libres
  useEffect(() => {
    if (!ups?.rack_id || !companyId) return;
    if (ups.location_id) {
      api.getRacks(ups.location_id, companyId).then(r => setRacks(r.data || [])).catch(() => {});
    }
    api.getRack(ups.rack_id, companyId)
      .then(res => setRackFreeUnits(res.data?.available_units_front || []))
      .catch(() => {});
  }, [ups?.rack_id, ups?.location_id, companyId]);

  const sf = (key, value) => { setForm(f => ({ ...f, [key]: value })); setIsDirty(true); };

  /* Descartar: recarga los valores guardados (no cierra la ficha). */
  const handleDiscard = () => {
    setReloadKey(k => k + 1);
    setIsDirty(false);
    setError(null);
  };

  // ── Ubicación / rack ───────────────────────────────────────────────────────
  const handleLocationChange = async (e) => {
    const lid = e.target.value;
    const loc = locations.find(l => String(l.id) === String(lid));
    setForm(f => ({
      ...f,
      location_id: lid,
      location_name: loc?.name || '',
      rack_id: '',
      rack_name: '',
      position_u: '',
    }));
    setIsDirty(true);
    setRackFreeUnits(null);
    try {
      const res = await api.getRacks(lid || null, companyId);
      setRacks(res.data || []);
    } catch (_) {}
  };

  const handleRackChange = async (e) => {
    const rid = e.target.value;
    const rack = racks.find(r => String(r.id) === String(rid));
    setForm(f => ({
      ...f,
      rack_id: rid,
      rack_name: rack?.name || '',
      location_id: rack?.location_id ? String(rack.location_id) : f.location_id,
      location_name: rack?.location_name || f.location_name,
      position_u: '',
      size_u: rack ? f.size_u : 1,
    }));
    setIsDirty(true);
    setRackFreeUnits(null);
    if (rid) {
      try {
        const res = await api.getRack(rid, companyId);
        setRackFreeUnits(res.data?.available_units_front || []);
      } catch (_) {}
    }
  };

  const filteredRacks = form.location_id
    ? racks.filter(r => String(r.location_id) === String(form.location_id))
    : racks;

  // Posiciones de inicio válidas en el rack (huecos consecutivos)
  const validPositions = useMemo(() => {
    if (rackFreeUnits === null) return null;
    const sizeVal = Number(form.size_u) || 1;
    const freeSet = new Set(rackFreeUnits);
    const currentU = Number(form.position_u);
    if (currentU) {
      for (let u = currentU; u < currentU + sizeVal; u++) freeSet.add(u);
    }
    return [...freeSet].sort((a, b) => a - b).filter(u => {
      for (let i = 0; i < sizeVal; i++) {
        if (!freeSet.has(u + i)) return false;
      }
      return true;
    });
  }, [rackFreeUnits, form.size_u, form.position_u]);

  const rangeLabel = useMemo(() => {
    const posVal = Number(form.position_u);
    const sizeVal = Number(form.size_u);
    if (!posVal || !sizeVal) return null;
    return `U${posVal} – U${posVal + sizeVal - 1}`;
  }, [form.position_u, form.size_u]);

  // ── Nombre sugerido ────────────────────────────────────────────────────────
  const handleSuggest = async () => {
    if (!companyId) return;
    setSuggesting(true);
    try {
      const resp = await api.suggestName({ type: 'ups', companyId, consume: true });
      const s = resp?.data?.suggested_name;
      if (s) sf('name', s);
    } catch (_) {}
    finally { setSuggesting(false); }
  };

  // ── Guardar / eliminar ─────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim()) { setError('El nombre es obligatorio'); return false; }
    try {
      setSaving(true);
      setError(null);
      const payload = {
        ...form,
        capacity_va:          num(form.capacity_va),
        capacity_w:           num(form.capacity_w),
        current_load_w:       num(form.current_load_w),
        outlets_count:        num(form.outlets_count),
        battery_quantity:     num(form.battery_quantity),
        battery_runtime_min:  num(form.battery_runtime_min),
        battery_install_date: dateOrNull(form.battery_install_date),
        battery_replace_date: dateOrNull(form.battery_replace_date),
        purchase_date:        dateOrNull(form.purchase_date),
        warranty_end:         dateOrNull(form.warranty_end),
        location_id:          form.location_id !== '' ? Number(form.location_id) : null,
        rack_id:              form.rack_id !== '' ? Number(form.rack_id) : null,
        position_u:           form.position_u !== '' ? Number(form.position_u) : null,
        size_u:               Number(form.size_u || 1),
      };
      let creado = null;
      if (isEditing) creado = registroGuardado(await api.updateUps(ups.id, payload, companyId));
      else           creado = registroGuardado(await api.createUps(payload, companyId));
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
      setIsDirty(false);
      // Se entrega al listado el registro recién guardado: el formulario sigue
      // abierto y pasa a modo edición, así se encienden las pestañas que solo
      // existen cuando el elemento ya está guardado (alimentación, docs…).
      onSave?.(creado);
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(
      `¿Eliminar el SAI "${form.name}"?\nSe eliminarán también sus asignaciones IPAM y su posición en el rack.`
    )) return;
    try {
      await api.deleteUps(ups.id, companyId);
      onSave();
      onClose();
    } catch (e) {
      setError(e.message);
    }
  };

  // ── Derivados ──────────────────────────────────────────────────────────────
  const statusInfo = STATUS_COLORS[form.status] || STATUS_COLORS.active;
  const powerLabel = [
    form.capacity_va ? `${form.capacity_va} VA` : null,
    form.capacity_w ? `${form.capacity_w} W` : null,
  ].filter(Boolean).join(' · ') || '—';
  const batteryLabel = form.battery_runtime_min
    ? `${form.battery_runtime_min} min`
    : (form.battery_quantity ? `${form.battery_quantity} uds.` : '—');
  const loadPct = useMemo(() => {
    const w = Number(form.current_load_w);
    const cap = Number(form.capacity_w);
    if (!w || !cap) return null;
    return Math.round((w / cap) * 1000) / 10;
  }, [form.current_load_w, form.capacity_w]);

  const filteredModelOptions = useMemo(() => {
    const matchedMfr = manufacturerOptions.find(
      m => (m.name || '').toLowerCase() === (form.vendor || '').toLowerCase()
    );
    const mid = matchedMfr?.id;
    return (modelOptions || []).filter(
      m => !mid || m.manufacturer_id === mid || !m.manufacturer_id
    );
  }, [modelOptions, manufacturerOptions, form.vendor]);

  // ── Catálogo de marcas / modelos ───────────────────────────────────────────
  const saveManufacturerToCatalog = async (name) => {
    const value = (name || '').trim();
    if (!value || !companyId) return;
    setCatalogSaving(true);
    setCatalogError(null);
    try {
      const exists = manufacturerOptions.some(
        m => (m.name || '').toLowerCase() === value.toLowerCase()
      );
      if (!exists) {
        await api.createManufacturer({ name: value, device_type: 'network' }, companyId);
      }
      await loadAux();
    } catch (e) {
      setCatalogError(e.message || 'No se pudo dar de alta la marca');
    } finally {
      setCatalogSaving(false);
    }
  };

  const saveModelToCatalog = async (modelName) => {
    const value = (modelName || '').trim();
    if (!value || !companyId) return;
    setCatalogSaving(true);
    setCatalogError(null);
    try {
      const matchedMfr = manufacturerOptions.find(
        m => (m.name || '').toLowerCase() === (form.vendor || '').toLowerCase()
      );
      await api.createDeviceModel(
        { name: value, device_type: 'network', manufacturer_id: matchedMfr?.id },
        companyId
      );
      await loadAux();
    } catch (e) {
      setCatalogError(e.message || 'No se pudo dar de alta el modelo');
    } finally {
      setCatalogSaving(false);
    }
  };

  const tabs = [
    { key: 'general', label: 'General' },
    { key: 'ubicacion', label: 'Ubicación' },
    { key: 'power', label: 'Potencia' },
    { key: 'battery', label: 'Baterías' },
    { key: 'access', label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` },
    { key: 'mgmt', label: 'Gestión' },
    ...(isEditing ? [{ key: 'pdu', label: 'Alimentación' }] : []),
  ];

  // En tarjetas, General y Ubicación se editan en General.
  const tabsUps = isCards
    ? [
        { key: 'general', label: 'General' },
        { key: 'power', label: 'Potencia' },
        { key: 'battery', label: 'Baterías' },
        { key: 'access', label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` },
        { key: 'mgmt', label: 'Gestión' },
        ...(isEditing ? [{ key: 'pdu', label: 'Alimentación' }] : []),
      ]
    : tabs;
  const paneClass = `dv-tab-pane${isCards ? ' sf2-pane' : ''}`;

  /* Pasar de ficha a ficha sin salir al listado (§12.A5). */
  const irASAI = (destino) => {
    if (!destino || saving) return;
    navGuard.request(() => {
      setIsDirty(false);
      setError(null);
      onNavigate?.(destino);
    });
  };

  /* Ubicación compartida: la pestaña de la ficha clásica y la columna derecha
     de General en tarjetas pintan exactamente lo mismo. Nombres y orden de
     servidores (Posición U y Tamaño U). */
  const ubicacionContent = (
    <>
      <div className="dv-field-grid">
        <div className="dv-field-group">
          <label className="dv-label">Ubicación</label>
          <LocationTreeSelect
            locations={locations}
            value={form.location_id}
            onChange={(id) => handleLocationChange({ target: { value: id } })}
            placeholder="— Sin ubicación —"
          />
        </div>
        <div className="dv-field-group">
          <label className="dv-label">Rack</label>
          <select className="dv-select" value={form.rack_id} onChange={handleRackChange}>
            <option value="">— Sin rack (torre / suelo) —</option>
            {filteredRacks.map(r => <option key={r.id} value={r.id}>{r.name}{r.code ? ` (${r.code})` : ''}</option>)}
          </select>
        </div>
        {form.rack_id && (
          <>
            <div className="dv-field-group">
              <label className="dv-label">Posición U</label>
              {validPositions !== null ? (
                <select className="dv-select" value={form.position_u} onChange={e => sf('position_u', e.target.value)}>
                  <option value="">— Sin posición —</option>
                  {validPositions.map(u => (
                    <option key={u} value={u}>U{u}</option>
                  ))}
                </select>
              ) : (
                <input
                  className="dv-input"
                  type="number"
                  min="1"
                  value={form.position_u}
                  onChange={e => sf('position_u', e.target.value)}
                />
              )}
              {rangeLabel && (
                <small style={{ color: '#64748b', fontSize: '0.75rem' }}>Ocupa {rangeLabel}</small>
              )}
            </div>
            <div className="dv-field-group">
              <label className="dv-label">Tamaño U</label>
              <input
                className="dv-input"
                type="number"
                min="1"
                max="12"
                value={form.size_u}
                onChange={e => sf('size_u', e.target.value)}
              />
            </div>
          </>
        )}
      </div>
      <p style={{ marginTop: 18, fontSize: '0.8rem', color: '#64748b' }}>
        El SAI puede quedarse solo con la ubicación; si indicas un rack aparecerá en el visor del armario como elemento «ups».
      </p>
    </>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="dv-overlay">
      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon />
          SAI
        </button>
        <DetailCrumb label={form.name || 'Nuevo SAI'} />

        {/* ── Navegación entre fichas: a la izquierda del spacer ── */}
        <FormPager
          items={navItems}
          index={navIndex}
          label="SAI"
          disabled={saving}
          onChange={irASAI}
        />
        {navGuard.dialog}

        <div className="dv-topbar-spacer" />
        {savedOk && (
          <span style={{ color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>✓ Guardado</span>
        )}
        {!isCards && (
        <span
          className="dv-status-badge"
          style={{ background: statusInfo.bg, color: statusInfo.color, cursor: 'pointer' }}
          onClick={() => {
            const statuses = ['active', 'standby', 'maintenance', 'decom'];
            const idx = statuses.indexOf(form.status);
            sf('status', statuses[(idx + 1) % statuses.length]);
          }}
        >
          {statusInfo.label}
        </span>
        )}
        <div className="dv-topbar-actions">
          {error && <span style={{ color: '#dc2626', fontSize: '0.82rem' }}>{error}</span>}
          {isCards ? (
            <>
              <button className="dv-btn-discard" onClick={onClose}>
                <XMarkIcon style={{ width: 15, height: 15 }} /> Cerrar
              </button>
              {isEditing && (
                <button className="dv-btn-delete" onClick={handleDelete}>
                  <TrashIcon style={{ width: 15, height: 15 }} />
                </button>
              )}
            </>
          ) : (
          <>
          <button className="dv-btn-discard" onClick={onClose}>
            <XMarkIcon style={{ width: 15, height: 15 }} /> Descartar
          </button>
          <button className="dv-btn-save" onClick={handleSave} disabled={saving}>
            <CheckIcon style={{ width: 15, height: 15 }} />
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
          {isEditing && (
            <button className="dv-btn-delete" onClick={handleDelete}>
              <TrashIcon style={{ width: 15, height: 15 }} />
            </button>
          )}
          </>
          )}
        </div>
      </div>

      {/* ── SmartBar (solo en la ficha clásica) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        <div className="dv-smart-btn" onClick={() => setTab('general')} style={{ cursor: 'pointer' }}>
          <span className="dv-smart-btn-icon">
            <BoltIcon style={{ width: 18, height: 18, color: '#f59e0b' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.78rem' }}>
            {form.form_factor === 'tower' ? 'Torre' : 'Rack'}
          </span>
          <span className="dv-smart-btn-label">Formato</span>
        </div>

        <div className="dv-smart-sep" />

        <div className="dv-smart-btn" onClick={() => setTab('power')} style={{ cursor: 'pointer' }}>
          <span className="dv-smart-btn-icon">
            <CpuChipIcon style={{ width: 18, height: 18, color: '#2563eb' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.78rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {powerLabel}
          </span>
          <span className="dv-smart-btn-label">Potencia</span>
        </div>

        <div className="dv-smart-sep" />

        <div className="dv-smart-btn" onClick={() => setTab('battery')} style={{ cursor: 'pointer' }}>
          <span className="dv-smart-btn-icon">
            <Battery50Icon style={{ width: 18, height: 18, color: '#16a34a' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.78rem' }}>{batteryLabel}</span>
          <span className="dv-smart-btn-label">Batería</span>
        </div>

        <div className="dv-smart-sep" />

        <div className="dv-smart-btn" onClick={() => setTab('mgmt')} style={{ cursor: 'pointer' }}>
          <span className="dv-smart-btn-icon">
            <GlobeAltIcon style={{ width: 18, height: 18, color: form.management_ip ? '#0369a1' : '#94a3b8' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.75rem', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {form.management_ip || '—'}
          </span>
          <span className="dv-smart-btn-label">Gestión</span>
        </div>

        <div className="dv-smart-sep" />

        <div className="dv-smart-btn" onClick={() => setTab('ubicacion')} style={{ cursor: 'pointer' }}>
          <span className="dv-smart-btn-icon">
            <MapPinIcon style={{ width: 18, height: 18, color: form.location_name ? '#0369a1' : '#94a3b8' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.78rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {form.location_name || '—'}
          </span>
          <span className="dv-smart-btn-label">Ubicación</span>
        </div>

        <div className="dv-smart-sep" />

        <div className="dv-smart-btn" onClick={() => setTab('ubicacion')} style={{ cursor: 'pointer' }}>
          <span className="dv-smart-btn-icon">
            <ServerStackIcon style={{ width: 18, height: 18, color: form.rack_name ? '#15803d' : '#94a3b8' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.78rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {form.rack_name || (form.form_factor === 'tower' ? 'Torre' : '—')}
          </span>
          <span className="dv-smart-btn-label">Rack</span>
        </div>

        {isEditing && (
          <DocSmartButton elementType="ups" elementId={ups.id} elementName={form.name} companyId={companyId} />
        )}
      </div>
      )}

      {/* ── Body ── */}
      <div className="dv-body">
        <div className={`dv-main${isCards ? ' sf2-main' : ''}`}>

          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={<BoltIcon style={{ width: 20, height: 20 }} />}
                title={form.name}
                onTitleChange={(v) => sf('name', v)}
                placeholder="Nombre del SAI…"
                editing
                saving={saving}
                onSave={handleSave}
                onCancel={onClose}
                crumb={[form.vendor, form.model, form.location_name, form.rack_name].filter(Boolean).join(' › ')}
                badges={[
                  { text: statusInfo.label, color: form.status === 'active' ? '#16a34a' : form.status === 'standby' ? '#0369a1' : form.status === 'maintenance' ? '#f59e0b' : '#dc2626' },
                  { text: form.form_factor === 'tower' ? 'Torre' : 'Rack' },
                  ...(loadPct != null ? [{ text: `Carga ${loadPct}%` }] : []),
                ]}
                chips={[
                  ...(form.capacity_va ? [{ value: `${form.capacity_va} VA`, label: 'potencia', onClick: () => setTab('power') }] : []),
                  ...(form.battery_runtime_min ? [{ value: `${form.battery_runtime_min} min`, label: 'autonomía', onClick: () => setTab('battery') }] : []),
                  ...(form.management_ip ? [{ value: form.management_ip, label: 'gestión', onClick: () => setTab('mgmt') }] : []),
                  ...(form.rack_name ? [{ value: form.rack_name, label: 'rack' }] : []),
                ]}
                extra={isEditing ? (
                  <DocSmartButton elementType="ups" elementId={ups.id} elementName={form.name} companyId={companyId} variant="chip" />
                ) : null}
              />
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    className="dv-record-title"
                    style={{ flex: 1 }}
                    value={form.name}
                    onChange={e => sf('name', e.target.value)}
                    placeholder="Nombre del SAI…"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleSuggest}
                    disabled={suggesting}
                    title="Sugerir nombre automático"
                    style={{ flexShrink: 0, padding: '4px 10px' }}
                  >
                    {suggesting ? '…' : <SparklesIcon style={{ width: 16, height: 16 }} />}
                  </button>
                </div>
              </>
            )}
            tabs={(
              <div className="dv-tabs-nav">
                {tabsUps.map(t => (
                  <button
                    key={t.key}
                    className={`dv-tab-btn${tab === t.key ? ' active' : ''}`}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}
          >
          <>

          {/* ── Tab: General / Resumen ── */}
          {tab === 'general' && (
            <div className={paneClass}>
              {isCards && (
                <div className="sf2-hl" style={{ marginBottom: 12 }}>
                  <div className="sf2-kpi"><span>Potencia</span><b style={{ fontSize: '0.95rem' }}>{powerLabel || '—'}</b></div>
                  <div className="sf2-kpi"><span>Carga</span><b style={{ fontSize: '0.95rem' }}>{loadPct != null ? `${loadPct}%` : '—'}</b></div>
                  <div className="sf2-kpi"><span>Autonomía</span><b style={{ fontSize: '0.95rem' }}>{batteryLabel || '—'}</b></div>
                </div>
              )}
              <div className={isCards ? 'sf2-grid sf2-grid--2' : undefined}>
              <Sf2Card isCards={isCards} title="Identificación y compra" editing>
              <div className="dv-field-grid">
                <div className="dv-field-group">
                  <label className="dv-label">Nombre *</label>
                  <input className="dv-input" value={form.name} onChange={e => sf('name', e.target.value)} placeholder="Ej. SAI-RACK-DC-01" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Fabricante / Marca</label>
                  <div className="d-flex gap-1">
                    <select
                      className="dv-select"
                      style={{ flex: '1 1 auto', minWidth: 0 }}
                      value={form.vendor}
                      onChange={e => { sf('vendor', e.target.value); setAddingManufacturer(false); setNewManufacturer(''); }}
                    >
                      <option value="">— Sin fabricante —</option>
                      {manufacturerOptions.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                      {form.vendor && !manufacturerOptions.find(m => m.name === form.vendor) && (
                        <option value={form.vendor}>{form.vendor}</option>
                      )}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm text-nowrap"
                      title="Dar de alta una marca en el maestro"
                      onClick={() => { setAddingManufacturer(v => !v); setCatalogError(null); }}
                    >
                      + Marca
                    </button>
                  </div>
                  {addingManufacturer && (
                    <div className="d-flex gap-1 mt-2">
                      <input
                        className="dv-input"
                        style={{ minWidth: 0 }}
                        placeholder="Nueva marca (ej. APC)"
                        value={newManufacturer}
                        onChange={e => setNewManufacturer(e.target.value)}
                        onKeyDown={async e => {
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          const name = newManufacturer.trim();
                          if (!name) return;
                          sf('vendor', name);
                          await saveManufacturerToCatalog(name);
                          setNewManufacturer('');
                          setAddingManufacturer(false);
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={catalogSaving || !newManufacturer.trim()}
                        onClick={async () => {
                          const name = newManufacturer.trim();
                          if (!name) return;
                          sf('vendor', name);
                          await saveManufacturerToCatalog(name);
                          setNewManufacturer('');
                          setAddingManufacturer(false);
                        }}
                      >
                        Guardar
                      </button>
                    </div>
                  )}
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Modelo</label>
                  <div className="d-flex gap-1">
                    <select
                      className="dv-select"
                      style={{ flex: '1 1 auto', minWidth: 0 }}
                      value={form.model}
                      onChange={e => { sf('model', e.target.value); setAddingModel(false); setNewModel(''); }}
                    >
                      <option value="">— Sin modelo —</option>
                      {filteredModelOptions.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                      {form.model && !modelOptions.find(m => m.name === form.model) && (
                        <option value={form.model}>{form.model}</option>
                      )}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm text-nowrap"
                      title="Dar de alta un modelo en el maestro"
                      onClick={() => { setAddingModel(v => !v); setCatalogError(null); }}
                    >
                      + Modelo
                    </button>
                  </div>
                  {addingModel && (
                    <div className="d-flex gap-1 mt-2">
                      <input
                        className="dv-input"
                        style={{ minWidth: 0 }}
                        placeholder={form.vendor ? `Nuevo modelo de ${form.vendor}` : 'Nuevo modelo'}
                        value={newModel}
                        onChange={e => setNewModel(e.target.value)}
                        onKeyDown={async e => {
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          const name = newModel.trim();
                          if (!name) return;
                          sf('model', name);
                          await saveModelToCatalog(name);
                          setNewModel('');
                          setAddingModel(false);
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={catalogSaving || !newModel.trim()}
                        onClick={async () => {
                          const name = newModel.trim();
                          if (!name) return;
                          sf('model', name);
                          await saveModelToCatalog(name);
                          setNewModel('');
                          setAddingModel(false);
                        }}
                      >
                        Guardar
                      </button>
                    </div>
                  )}
                  {addingModel && !form.vendor && (
                    <small className="text-muted d-block mt-1">
                      Elige primero el fabricante para asociar el modelo a esa marca.
                    </small>
                  )}
                  {catalogError && (
                    <small className="text-danger d-block mt-1">{catalogError}</small>
                  )}
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Formato</label>
                  <select className="dv-select" value={form.form_factor} onChange={e => sf('form_factor', e.target.value)}>
                    {FORM_FACTORS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Tecnología</label>
                  <select className="dv-select" value={form.technology} onChange={e => sf('technology', e.target.value)}>
                    {TECHNOLOGIES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Nº de serie</label>
                  <input className="dv-input" value={form.serial_number} onChange={e => sf('serial_number', e.target.value)} />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Asset tag</label>
                  <input className="dv-input" value={form.asset_tag} onChange={e => sf('asset_tag', e.target.value)} />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Estado</label>
                  <select className="dv-select" value={form.status} onChange={e => sf('status', e.target.value)}>
                    <option value="active">Activo</option>
                    <option value="standby">Standby</option>
                    <option value="maintenance">Mantenimiento</option>
                    <option value="decom">Decommissioned</option>
                  </select>
                </div>
              </div>

              <div className="dv-section-header" style={{ marginTop: 28 }}>Compra y garantía</div>
              <div className="dv-field-grid">
                <div className="dv-field-group">
                  <label className="dv-label">Fecha de compra</label>
                  <input className="dv-input" type="date" value={form.purchase_date} onChange={e => sf('purchase_date', e.target.value)} />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Fin de garantía</label>
                  <input className="dv-input" type="date" value={form.warranty_end} onChange={e => sf('warranty_end', e.target.value)} />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Proveedor</label>
                  <input className="dv-input" value={form.supplier} onChange={e => sf('supplier', e.target.value)} placeholder="Ej. Distribuidor / integrador" />
                </div>
              </div>

              <div className="dv-section-header" style={{ marginTop: 28 }}>Notas</div>
              <textarea
                className="dv-input"
                rows={4}
                value={form.notes}
                onChange={e => sf('notes', e.target.value)}
                placeholder="Observaciones, incidencias, contrato de mantenimiento…"
                style={{ width: '100%', resize: 'vertical' }}
              />
              </Sf2Card>
              {isCards && (
                <Sf2Card isCards title="Ubicación" editing>{ubicacionContent}</Sf2Card>
              )}
              </div>
            </div>
          )}

          {/* ── Tab: Potencia ── */}
          {tab === 'power' && (
            <div className={paneClass}>
              <div className="dv-section-header">Potencia nominal y carga</div>
              <div className="dv-field-grid">
                <div className="dv-field-group">
                  <label className="dv-label">Potencia aparente (VA)</label>
                  <input className="dv-input" type="number" min="0" value={form.capacity_va} onChange={e => sf('capacity_va', e.target.value)} placeholder="1500" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Potencia activa (W)</label>
                  <input className="dv-input" type="number" min="0" value={form.capacity_w} onChange={e => sf('capacity_w', e.target.value)} placeholder="900" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Carga actual (W)</label>
                  <input className="dv-input" type="number" min="0" value={form.current_load_w} onChange={e => sf('current_load_w', e.target.value)} placeholder="450" />
                  {loadPct != null && (
                    <small style={{ color: loadPct > 80 ? '#dc2626' : '#64748b', fontSize: '0.75rem' }}>
                      {loadPct}% de la potencia nominal
                    </small>
                  )}
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Fases de entrada</label>
                  <select className="dv-select" value={form.phases} onChange={e => sf('phases', e.target.value)}>
                    {PHASES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="dv-section-header" style={{ marginTop: 28 }}>Entradas y salidas</div>
              <div className="dv-field-grid">
                <div className="dv-field-group">
                  <label className="dv-label">Tensión de entrada (V)</label>
                  <input className="dv-input" value={form.input_voltage} onChange={e => sf('input_voltage', e.target.value)} placeholder="230 / 230/400" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Tensión de salida (V)</label>
                  <input className="dv-input" value={form.output_voltage} onChange={e => sf('output_voltage', e.target.value)} placeholder="230" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Nº de tomas de salida</label>
                  <input className="dv-input" type="number" min="0" value={form.outlets_count} onChange={e => sf('outlets_count', e.target.value)} placeholder="8" />
                  <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
                    Al guardar se generan las tomas y podrás conectarlas en «Alimentación».
                  </small>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Tipos de toma</label>
                  <input className="dv-input" value={form.outlet_types} onChange={e => sf('outlet_types', e.target.value)} placeholder="8×C13 + 1×C19" />
                  <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
                    Formato «cantidad×tipo». Se usa para etiquetar cada toma al crearla.
                  </small>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Bypass manual</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                    <input type="checkbox" id="has_bypass" checked={form.has_bypass} onChange={e => sf('has_bypass', e.target.checked)} style={{ width: 16, height: 16 }} />
                    <label htmlFor="has_bypass" style={{ fontSize: '0.85rem', color: '#334155', cursor: 'pointer' }}>Incluye bypass</label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Baterías ── */}
          {tab === 'battery' && (
            <div className={paneClass}>
              <div className="dv-section-header">Baterías</div>
              <div className="dv-field-grid">
                <div className="dv-field-group">
                  <label className="dv-label">Tipo</label>
                  <select className="dv-select" value={form.battery_type} onChange={e => sf('battery_type', e.target.value)}>
                    <option value="">— Sin especificar —</option>
                    {BATTERY_TYPES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                  </select>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Nº de baterías / módulos</label>
                  <input className="dv-input" type="number" min="0" value={form.battery_quantity} onChange={e => sf('battery_quantity', e.target.value)} placeholder="4" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Autonomía a plena carga (min)</label>
                  <input className="dv-input" type="number" min="0" value={form.battery_runtime_min} onChange={e => sf('battery_runtime_min', e.target.value)} placeholder="10" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Fecha de instalación</label>
                  <input className="dv-input" type="date" value={form.battery_install_date} onChange={e => sf('battery_install_date', e.target.value)} />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Próxima sustitución</label>
                  <input className="dv-input" type="date" value={form.battery_replace_date} onChange={e => sf('battery_replace_date', e.target.value)} />
                  <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
                    Se usa para planificar el mantenimiento preventivo.
                  </small>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Gestión ── */}
          {tab === 'mgmt' && (
            <div className={paneClass}>
              <div className="dv-section-header">Tarjeta de gestión / red</div>
              <div className="dv-field-grid">
                <div className="dv-field-group">
                  <label className="dv-label">Tarjeta de red / SNMP</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                    <input type="checkbox" id="network_card" checked={form.network_card} onChange={e => sf('network_card', e.target.checked)} style={{ width: 16, height: 16 }} />
                    <label htmlFor="network_card" style={{ fontSize: '0.85rem', color: '#334155', cursor: 'pointer' }}>Instalada</label>
                  </div>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">IP de gestión</label>
                  <input className="dv-input" value={form.management_ip} onChange={e => sf('management_ip', e.target.value)} placeholder="10.0.0.25" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">MAC de gestión</label>
                  <input className="dv-input" value={form.management_mac} onChange={e => sf('management_mac', e.target.value)} placeholder="aa:bb:cc:dd:ee:ff" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Firmware</label>
                  <input className="dv-input" value={form.firmware_version} onChange={e => sf('firmware_version', e.target.value)} />
                </div>
              </div>
              <p style={{ marginTop: 18, fontSize: '0.8rem', color: '#64748b' }}>
                La IP puede registrarse también en IPAM con el tipo de objeto «SAI» para
                controlar subred, VLAN y conflictos.
              </p>
            </div>
          )}

          {/* ── Tab: Accesos ── */}
          {tab === 'access' && (
            <div className={paneClass}>
              <div className="dv-section">
                <DeviceAccessPanel
                  objectType="ups"
                  objectId={ups?.id}
                  companyId={companyId}
                  elementName={ups?.name || form.name}
                  defaultHost={form.management_ip}
                  onCountChange={setAccessCount}
                />
              </div>
            </div>
          )}

          {/* ── Tab: Ubicación (ficha clásica; en tarjetas va en General) ── */}
          {!isCards && tab === 'ubicacion' && (
            <div className={paneClass}>
              <Sf2Card isCards={isCards} title="Ubicación física" editing>{ubicacionContent}</Sf2Card>
            </div>
          )}

          {/* ── Tab: Alimentación (entrada PDU + salidas del SAI) ── */}
          {tab === 'pdu' && (
            <div className={paneClass}>
              {ups?.rack_element_id ? (
                <>
                  <div className="dv-section">
                    <div className="dv-section-title">Entrada del SAI (desde PDU o toma)</div>
                    <PowerConnectionsPanel
                      consumerType="rack_element"
                      consumerId={ups.rack_element_id}
                      companyId={companyId}
                      rackId={form.rack_id ? Number(form.rack_id) : undefined}
                      elementName={form.name}
                    />
                  </div>

                  <div className="dv-section" style={{ marginTop: 24 }}>
                    <div className="dv-section-title">Salidas del SAI</div>
                    <UpsOutletsPanel
                      key={`${ups.id}-${ups.outlets_total ?? 0}`}
                      upsId={ups.id}
                      companyId={companyId}
                      outletsCount={Number(form.outlets_count) || 0}
                      onChanged={() => onSave?.(null)}
                    />
                  </div>
                </>
              ) : (
                <div className="dv-section">
                  <p className="dv-hint">
                    {Number(form.outlets_count) > 0
                      ? 'Guarda el SAI con una ubicación o rack para generar sus tomas de salida.'
                      : 'Indica un rack o ubicación y el nº de tomas de salida para gestionar la alimentación.'}
                  </p>
                </div>
              )}
            </div>
          )}
          </>
          <FormDirtyDock show={isCards && isDirty} saving={saving} onDiscard={handleDiscard} onSave={handleSave} />
          </FormCardsShell>
        </div>

        <CiActivityPanel
          companyId={companyId}
          ciType="ups"
          ciId={ups?.id}
          tipoLegible="el SAI"
        />
      </div>
    </div>
  );
}
